import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle } from "lucide-react";

interface QuestionReviewProps {
  questionNumber: number;
  question: {
    stem: string;
    option_a: string;
    option_b: string;
    option_c: string;
    option_d?: string;
    option_e?: string;
    correct_option: "A" | "B" | "C" | "D" | "E";
    explanation?: string;
  };
  userAnswer: string | null;
  isCorrect: boolean;
}

export function QuestionReview({
  questionNumber,
  question,
  userAnswer,
  isCorrect,
}: QuestionReviewProps) {
  const options = [
    { label: "A", text: question.option_a },
    { label: "B", text: question.option_b },
    { label: "C", text: question.option_c },
    question.option_d && { label: "D", text: question.option_d },
    question.option_e && { label: "E", text: question.option_e },
  ].filter(Boolean) as { label: string; text: string }[];

  return (
    <Card className={isCorrect ? "border-success/50" : "border-destructive/50"}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-base">
              Question {questionNumber}
            </Badge>
            {isCorrect ? (
              <CheckCircle2 className="h-5 w-5 text-success" />
            ) : (
              <XCircle className="h-5 w-5 text-destructive" />
            )}
          </div>
          <Badge className={isCorrect ? "bg-success" : "bg-destructive"}>
            {isCorrect ? "Correct" : "Incorrect"}
          </Badge>
        </div>

        <div className="mb-4">
          <p className="text-foreground leading-relaxed">{question.stem}</p>
        </div>

        <div className="space-y-2 mb-4">
          {options.map((option) => {
            const isUserAnswer = userAnswer === option.label;
            const isCorrectAnswer = question.correct_option === option.label;

            let bgColor = "bg-muted/50";
            let borderColor = "border-transparent";
            let textColor = "text-foreground";

            if (isCorrectAnswer) {
              bgColor = "bg-success/10";
              borderColor = "border-success";
              textColor = "text-success-foreground";
            } else if (isUserAnswer && !isCorrect) {
              bgColor = "bg-destructive/10";
              borderColor = "border-destructive";
              textColor = "text-destructive-foreground";
            }

            return (
              <div
                key={option.label}
                className={`p-4 rounded-lg border-2 ${bgColor} ${borderColor} transition-colors`}
              >
                <div className="flex items-start gap-3">
                  <span className={`font-bold ${textColor} min-w-[24px]`}>
                    {option.label}.
                  </span>
                  <span className={textColor}>{option.text}</span>
                  {isCorrectAnswer && (
                    <CheckCircle2 className="h-5 w-5 text-success ml-auto flex-shrink-0" />
                  )}
                  {isUserAnswer && !isCorrect && (
                    <XCircle className="h-5 w-5 text-destructive ml-auto flex-shrink-0" />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {question.explanation && (
          <div className="mt-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
            <p className="text-sm font-semibold text-primary mb-2">Explanation:</p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {question.explanation}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
